import React, { useState, useEffect } from 'react';
import { Box, Button, Stack, Typography, createTheme, ThemeProvider } from '@mui/material';
import styles from '../../../../assets/css/components/MobileLobby.module.css';
import MobileHeader from '../../../Header/MobileHeader';
import { IUserState } from '../../../../Core/State/IUserState';
import { InteractionHandler } from '../../../../Core/Handler/InteractionHandler';
import MobileRoomBar from '../../../RoomBar/MobileRoomBar';
import DesktopTournamentLobbyModal from './DesktopTournamentLobbyModal';
import MobileTournamentTableRow from './MobileTournamentTableRow';
import { ILobbyTournamentState } from '../../../../Core/State/ILobbyTournamentState';
import {
    buildTournamentLobbyLiveRowsRequest,
    buildTournamentLobbyDetailsRequest,
    buildTournamentScheduleFilters,
    formatTournamentScheduleGlobalSegmentLabel,
    formatTournamentScheduleSegmentLabel,
    getDefaultTournamentScheduleFilterState,
    getTournamentScheduleCurrentSegmentStartIndex,
    getTournamentScheduleSegmentFromStartIndex,
    getTournamentLobbyDetailsTournament,
    getTournamentLobbyLiveRowsForRequest,
    isTournamentScheduleGlobalView,
    isTournamentScheduleCurrentTimeAnchoredPreset,
    isTournamentScheduleDateToday,
    logTournamentLobbyTournamentDebug,
    mergeScheduleTournamentsWithLobbyLiveRows,
    mergeScheduleTournamentsWithRealtime,
    shiftTournamentScheduleDate,
    TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE,
    useLobbyTournamentSchedule,
    useTournamentLobbyDetails,
    useTournamentLobbyLiveRows,
    type TournamentScheduleFilterState
} from '../../../../Tournament/TournamentSchedule';
import TournamentScheduleFilterBar from './TournamentScheduleFilterBar';

interface MobileMttLobbyProps {
    setSection: (section: string) => void;
    section: string;
    lobbyState: ILobbyTournamentState;
    userState: IUserState;
    interactionHandler: InteractionHandler;
    logout: () => void;
    userConnectedCount: number;
}

const MobileMttLobby: React.FC<MobileMttLobbyProps> = ({ userState, section, logout, userConnectedCount, interactionHandler }) => {

    const [modalOpen, setModalOpen] = useState(false);
    const [modalTournamentId, setModalTournamentId] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [scheduleFilters, setScheduleFilters] = useState<TournamentScheduleFilterState>(() => getDefaultTournamentScheduleFilterState());
    const [scheduleSegmentStartIndex, setScheduleSegmentStartIndex] = useState<number | null>(null);
    const scheduleQueryFilters = React.useMemo(() => buildTournamentScheduleFilters(scheduleFilters), [scheduleFilters]);
    const isScheduleGlobalPagination = isTournamentScheduleGlobalView(scheduleFilters);
    const scheduleQuery = useLobbyTournamentSchedule({ filters: scheduleQueryFilters });

    const handleClick = (gameId: string) => {
        setModalTournamentId(gameId);
        setModalOpen(true);
    }

    const handleModalClose = () => {
        setModalOpen(false);
        setModalTournamentId(null);
    };

    const handleScheduleFilterChange = (nextFilters: TournamentScheduleFilterState) => {
        setScheduleFilters(nextFilters);
        setScheduleSegmentStartIndex(null);
    };

    const clearSchedulePresetHighlight = () => {
        setScheduleFilters((prev) => (
            prev.preset === 'CUSTOM' ? prev : { ...prev, preset: 'CUSTOM' }
        ));
    };

    const shiftScheduleDay = (days: number) => {
        setScheduleSegmentStartIndex(days < 0 ? Number.MAX_SAFE_INTEGER : 0);
        setScheduleFilters((prev) => ({
            ...prev,
            date: shiftTournamentScheduleDate(prev.date, days),
            preset: 'CUSTOM'
        }));
    };

    const handlePreviousSchedulePage = () => {
        if (schedulePage.startIndex > 0) {
            setScheduleSegmentStartIndex(Math.max(0, schedulePage.startIndex - TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE));
            if (!isScheduleGlobalPagination) clearSchedulePresetHighlight();
            return;
        }

        if (isScheduleGlobalPagination) return;
        shiftScheduleDay(-1);
    };

    const handleNextSchedulePage = () => {
        if (schedulePage.endItemNumber < schedulePage.totalItems) {
            setScheduleSegmentStartIndex(schedulePage.endItemNumber);
            if (!isScheduleGlobalPagination) clearSchedulePresetHighlight();
            return;
        }

        if (isScheduleGlobalPagination) return;
        shiftScheduleDay(1);
    };

    // Update current time every second for real-time calculations
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        
        return () => clearInterval(timer);
    }, []);

    const darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751",
            },
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    const sortedScheduleTournaments = React.useMemo(() => {
        return [...scheduleQuery.tournaments].sort((a, b) => {
            const aStart = new Date(a.startTime).getTime();
            const bStart = new Date(b.startTime).getTime();
            return aStart - bStart;
        });
    }, [scheduleQuery.tournaments]);
    const shouldAnchorCurrentSegment = (
        scheduleSegmentStartIndex === null &&
        isTournamentScheduleCurrentTimeAnchoredPreset(scheduleFilters.preset) &&
        isTournamentScheduleDateToday(scheduleFilters.date, currentTime)
    );
    const currentScheduleStartIndex = shouldAnchorCurrentSegment
        ? getTournamentScheduleCurrentSegmentStartIndex(sortedScheduleTournaments, currentTime)
        : null;
    const activeScheduleStartIndex = currentScheduleStartIndex ?? scheduleSegmentStartIndex ?? 0;
    const schedulePage = React.useMemo(
        () => getTournamentScheduleSegmentFromStartIndex(sortedScheduleTournaments, activeScheduleStartIndex),
        [sortedScheduleTournaments, activeScheduleStartIndex]
    );
    const lobbyLiveRowsRequest = React.useMemo(
        () => buildTournamentLobbyLiveRowsRequest(schedulePage.items),
        [schedulePage.items]
    );
    const lobbyLiveRowsQuery = useTournamentLobbyLiveRows(lobbyLiveRowsRequest);
    const lobbyLiveTournamentRows = React.useMemo(
        () => getTournamentLobbyLiveRowsForRequest(lobbyLiveRowsRequest, lobbyLiveRowsQuery.data),
        [lobbyLiveRowsRequest, lobbyLiveRowsQuery.data]
    );
    const visibleTournaments = React.useMemo(
        () => mergeScheduleTournamentsWithLobbyLiveRows(schedulePage.items, lobbyLiveTournamentRows),
        [schedulePage.items, lobbyLiveTournamentRows]
    );

    const modalBaseTournament = React.useMemo(() => (
        modalTournamentId
            ? visibleTournaments.find(t => t.gameId === modalTournamentId || t.tournamentId === modalTournamentId) ||
                sortedScheduleTournaments.find(t => t.gameId === modalTournamentId || t.tournamentId === modalTournamentId) ||
                null
            : null
    ), [modalTournamentId, visibleTournaments, sortedScheduleTournaments]);
    const modalDetailsRequest = React.useMemo(
        () => buildTournamentLobbyDetailsRequest(modalTournamentId),
        [modalTournamentId]
    );
    const modalDetailsQuery = useTournamentLobbyDetails(modalDetailsRequest);
    const modalDetailsTournament = React.useMemo(
        () => getTournamentLobbyDetailsTournament(modalDetailsQuery.data),
        [modalDetailsQuery.data]
    );
    const modalTournament = React.useMemo(() => {
        if (!modalBaseTournament) return modalDetailsTournament;
        return mergeScheduleTournamentsWithRealtime(
            [modalBaseTournament],
            modalDetailsTournament ? [modalDetailsTournament] : []
        )[0] || modalBaseTournament;
    }, [modalBaseTournament, modalDetailsTournament]);

    useEffect(() => {
        logTournamentLobbyTournamentDebug('mobile-modal-tournament', modalTournament, {
            modalTournamentId,
        });
    }, [modalTournament, modalTournamentId]);

    const scheduleDateLabel = isScheduleGlobalPagination
        ? formatTournamentScheduleGlobalSegmentLabel(schedulePage)
        : formatTournamentScheduleSegmentLabel(scheduleFilters.date, schedulePage);
    const tournamentCountLabel = scheduleQuery.isFetching
        ? 'Refreshing...'
        : `${schedulePage.startItemNumber}-${schedulePage.endItemNumber} of ${schedulePage.totalItems} tournaments`;
    const previousSchedulePageDisabled = scheduleQuery.isFetching || (
        isScheduleGlobalPagination && schedulePage.startIndex <= 0
    );
    const nextSchedulePageDisabled = scheduleQuery.isFetching || (
        isScheduleGlobalPagination && schedulePage.endItemNumber >= schedulePage.totalItems
    );

    return (
        <ThemeProvider theme={darkTheme}>
            <Stack className={styles.mobileLobby}>
                <MobileHeader 
                    userState={userState}
                    section={section}
                    logout={logout}
                />
                <MobileRoomBar
                    userConnectedCount={userConnectedCount}
                />
                <Box sx={{ px: 1, pt: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setFiltersOpen((open) => !open)}
                            sx={{ fontWeight: 700 }}
                        >
                            Filters
                        </Button>
                        <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '12px' }}>
                            {tournamentCountLabel}
                        </Typography>
                    </Stack>
                    {filtersOpen && (
                        <Box sx={{ mt: 1 }}>
                            <TournamentScheduleFilterBar
                                compact
                                showPresets
                                value={scheduleFilters}
                                onChange={handleScheduleFilterChange}
                            />
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={previousSchedulePageDisabled}
                                    onClick={handlePreviousSchedulePage}
                                    sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.2)' }}
                                >
                                    Previous
                                </Button>
                                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '12px' }}>
                                    {scheduleDateLabel}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={nextSchedulePageDisabled}
                                    onClick={handleNextSchedulePage}
                                    sx={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.2)' }}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Box>
                <Stack spacing={1} className={styles.mobileLobbyTables}>
                    {visibleTournaments.map((tournament) => (
                        <MobileTournamentTableRow
                            key={tournament.gameId}
                            tournament={tournament}
                            userState={userState}
                            handleClick={handleClick}
                            interactionHandler={interactionHandler}
                        />
                    ))}
                </Stack>
            </Stack>
            {modalOpen && (
                <DesktopTournamentLobbyModal
                    open={modalOpen}
                    tournament={modalTournament}
                    userState={userState}
                    interactionHandler={interactionHandler}
                    onClose={handleModalClose}
                />
            )}
        </ThemeProvider>
    );
};

export default MobileMttLobby;
